import { useEffect } from "react";
import { useNavigate, useOutlet } from "react-router-dom";
import { useAppAbility } from "@/casl";
import { AppAction, AppSubject } from "@/casl/types";
import { Container } from "@/components/atoms";
import { Tabs } from "@/components/molecules";
import { PageHeader } from "@/components/organisms";
import { useOperator } from "@/hooks";
import { Subscriptions } from "../Subscriptions/Subscriptions";
import { CreditCardPayments } from "./CreditCardPayments";
import { DebitHistory } from "./DebitHistory";
import { Invoicing } from "./Invoicing";
import { MonthlyInvoice } from "./MonthlyInvoice";
import { PaymentConfiguration } from "./PaymentConfiguration";
import { PayoutHistory } from "./PayoutHistory";
import { SubscriptionPlanPage } from "./SubscriptionPlanPage";
import { SubscriptionsTab } from "./SubscriptionsTab";

type Tab = {
  name: string;
  to: string;
  permission?: [AppAction, AppSubject];
  isPermissionLocked?: boolean;
  requiredRoles?: string[];
};

const pageTabs: Tab[] = [
  {
    name: "Credit Card Payments",
    to: "credit-card-payments",
    permission: ["view", "providerPayments"],
  },
  {
    name: "Invoicing",
    to: "invoicing",
    permission: ["manage", "providerPaymentsInvoice"],
  },
  {
    name: "Payout History",
    to: "payout-history",
    permission: ["view", "providerPayments"],
  },
  {
    name: "Debit History",
    to: "debit-history",
    permission: ["view", "providerPayments"],
  },
  {
    name: "Payment Configuration",
    to: "payment-configuration",
    permission: ["manage", "providerPaymentsConfig"],
    isPermissionLocked: true,
  },
  {
    name: "Subscriptions",
    to: "subscriptions",
    requiredRoles: ["Owner", "Fa_Access"],
    isPermissionLocked: true,
  },
  {
    name: "Monthly Invoice",
    to: "monthly-invoice",
  },
];

export const Payments = () => {
  const outlet = useOutlet();
  const navigate = useNavigate();
  const ability = useAppAbility();
  const { roles } = useOperator();

  useEffect(() => {
    if (!outlet) navigate("../");
  }, [outlet, navigate]);

  return (
    <Container className="mb-12 flex w-full flex-1 flex-col">
      <PageHeader skipCrumbs={1} title="Payments" />
      <Tabs className="mb-4 border-b !border-neutral-mid-gray">
        {pageTabs.map((t, i) => {
          const { permission, to, name, isPermissionLocked, requiredRoles } = t;
          if (requiredRoles) {
            const hasRole = requiredRoles.some((role) => roles.includes(role));
            if (hasRole) {
              return <Tabs.Tab key={i} to={to} name={name} />;
            } else {
              if (isPermissionLocked) return <Tabs.Tab key={i} to={to} name={name} isLocked={true} />;
            }
          } else if (permission) {
            if (ability.can(permission[0], permission[1])) {
              return <Tabs.Tab key={i} to={to} name={name} />;
            } else {
              if (isPermissionLocked) return <Tabs.Tab key={i} to={to} name={name} isLocked={true} />;
            }
          } else {
            return <Tabs.Tab key={i} to={to} name={name} />;
          }
        })}
      </Tabs>
      {outlet}
    </Container>
  );
};

Payments.Invoicing = Invoicing;
Payments.CreditCardPayments = CreditCardPayments;
Payments.PayoutHistory = PayoutHistory;
Payments.DebitHistory = DebitHistory;
Payments.PaymentConfiguration = PaymentConfiguration;
Payments.SubscriptionsTab = SubscriptionsTab;
Payments.SubscriptionPlanPage = SubscriptionPlanPage;
Payments.MonthlyInvoice = MonthlyInvoice;
Payments.Subscriptions = Subscriptions;
